import {
  Button,
  Flex,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { getDashboardUrl, getWebUrl } from "@ui/../config/src";
import { Fragment } from "react";
import { FaCheck } from "react-icons/fa";
import { PricingTableProps } from "./pricing-table";

export const MobilePricingTable = ({
  pricingOptions,
  pricingPlans,
  pricingTableRows,
}: PricingTableProps) => {
  return (
    <TableContainer
      border="1px solid black"
      borderBottom="none"
      borderRadius="lg"
    >
      <Table
        size={{
          base: "small",
          sm: "md",
        }}
      >
        <Thead>
          <Tr bgColor={"white"}>
            {pricingPlans.map((plan, idx) => (
              <Th
                key={`pricing-plan-${idx}`}
                border="1px solid black"
                borderTop="none"
                borderLeft={{
                  base: "none",
                  md: "unset",
                }}
                borderRight={idx === 1 ? "none" : ""}
                px={{
                  base: 1,
                  md: 3,
                }}
              >
                <VStack
                  pb={{
                    md: 7,
                  }}
                >
                  <Text
                    color="#0284C7"
                    textTransform="capitalize"
                    fontSize={{
                      base: 12,
                      md: 16,
                    }}
                    fontWeight="extrabold"
                    mt={{
                      md: idx === 1 ? 7 : 5,
                    }}
                  >
                    {plan.planName}
                  </Text>
                  {idx === 0 ? (
                    <Flex
                      flexDir={"row"}
                      alignItems={"center"}
                      gap={2}
                      filter={`${plan.planName === "Pro" ? "blur(7px)" : ""}`}
                    >
                      <Text
                        textTransform="capitalize"
                        color="black"
                        fontSize={{
                          base: 18,
                          md: 28,
                        }}
                        fontWeight="extrabold"
                        pt={{ base: 0, md: 3 }}
                      >
                        {pricingOptions[`${idx}`].price}
                      </Text>
                      <Flex
                        textTransform="lowercase"
                        flexDir={{
                          base: "column",
                          md: "row",
                        }}
                        fontWeight="medium"
                        fontSize={12}
                        pt={{ base: 0, md: 3 }}
                      >
                        <Text
                          pr={{
                            base: 0,
                            md: 1,
                          }}
                        >
                          per
                        </Text>
                        <Text>report</Text>
                      </Flex>
                    </Flex>
                  ) : (
                    <Text
                      textTransform="capitalize"
                      color="black"
                      fontSize={{
                        base: 18,
                        md: 28,
                      }}
                      fontWeight="extrabold"
                      pb={{ md: 2 }}
                      pt={{ base: 4, md: 3 }}
                    >
                      {pricingOptions[`${idx}`].price}
                    </Text>
                  )}
                  <Text
                    textTransform="none"
                    fontWeight="medium"
                    fontStyle="italic"
                    color="#1d4ed8"
                    lineHeight={1.5}
                    textAlign="center"
                    maxW={"210px"}
                    whiteSpace={idx === 0 ? "pre-line" : "normal"}
                    pt={{
                      md: idx === 0 ? 2 : 0,
                    }}
                  >
                    {plan.tableSubtitle}
                  </Text>
                </VStack>
              </Th>
            ))}
          </Tr>
          {pricingPlans.map((plan, idx) => (
            <Td
              key={`pricing-plan-inner-${idx}`}
              border="1px solid black"
              px={0}
              py={2}
              borderRight={idx === 1 ? "none" : ""}
              borderLeft={idx === 0 ? "none" : ""}
            >
              <Flex justify="center">
                {idx === 0 ? (
                  <Button
                    disabled
                    cursor={"unset"}
                    w="min-content"
                    px={1.5}
                    fontSize={11}
                    bgColor="#1d4ed8"
                    color="white"
                    h="30px"
                    borderRadius={4}
                    _hover={{
                      bgColor: "#1d4ed8",
                    }}
                  >
                    {plan.mobileButtonText}
                  </Button>
                ) : (
                  <Link href={getWebUrl() + plan.mobileButtonLink}>
                    <Button
                      w="min-content"
                      px={1.5}
                      fontSize={11}
                      bgColor="#1d4ed8"
                      color="white"
                      h="30px"
                      borderRadius={4}
                    >
                      {plan.mobileButtonText}
                    </Button>
                  </Link>
                )}
              </Flex>
            </Td>
          ))}
        </Thead>
        <Tbody>
          {pricingTableRows.slice(0, 3).map((row, rowIdx) => (
            <Fragment key={`pricing-table-row-${rowIdx}`}>
              <Tr>
                <Td
                  colSpan={4}
                  borderBottom="none"
                  bgColor="gray.200"
                  color="#1d4ed8"
                  fontWeight="bold"
                  py={3}
                  fontSize={{
                    base: 12,
                    sm: 14,
                  }}
                  pl={2}
                >
                  {row.rowName}
                </Td>
              </Tr>
              <Tr h={{ base: "40px", sm: "unset" }}>
                {Object.values(row)
                  .slice(1)
                  .map((content: string, cellIdx) => (
                    <Fragment key={`pricing-table-row-content-${cellIdx}`}>
                      {content && (
                        <Td
                          key={`pricing-table-row-content-${cellIdx}`}
                          border="1px solid black"
                          textAlign="center"
                          borderRight={"none"}
                          borderLeft={cellIdx === 1 ? "none" : ""}
                          fontSize={{
                            base: 12,
                            sm: 14,
                          }}
                          filter={`${
                            cellIdx === 1 && rowIdx === 2 ? "blur(7px)" : ""
                          }`}
                        >
                          {content}
                        </Td>
                      )}
                    </Fragment>
                  ))}
              </Tr>
            </Fragment>
          ))}
          {pricingTableRows.slice(3).map((row, idx) => (
            <Fragment key={`pricing-table-row-inner-${idx}`}>
              <Tr>
                <Td
                  colSpan={4}
                  borderBottom="none"
                  bgColor="gray.200"
                  color="#1d4ed8"
                  fontWeight="bold"
                  py={3}
                  fontSize={{
                    base: 12,
                    sm: 14,
                  }}
                  pl={2}
                >
                  {row.rowName}
                </Td>
              </Tr>
              <Tr h={{ base: "40px", sm: "unset" }}>
                {Object.values(row)
                  .slice(1)
                  .map((content: boolean, idx) => (
                    <Fragment key={`pricing-table-row-inner-content-${idx}`}>
                      {content !== null && (
                        <Td
                          border="1px solid black"
                          borderRight={"none"}
                          borderLeft={idx === 1 ? "none" : ""}
                        >
                          <Flex
                            justifyContent="center"
                            fontSize={{
                              base: 12,
                              sm: 14,
                            }}
                          >
                            {content ? <FaCheck /> : <></>}
                          </Flex>
                        </Td>
                      )}
                    </Fragment>
                  ))}
              </Tr>
            </Fragment>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
